import {Contact, ContactEmail, Locale, NotificationSetting, User} from "models"
import React, {useMemo, useRef} from "react"
import {ShapeComponent, shapeComponent} from "set-state-compare/build/shape-component"
import AppHistory from "shared/history"
import Card from "components/utils/card"
import changeLocale from "components/users/change-locale"
import {withInputContainer as CircleColorSelect} from "components/users/circle-color-select"
import {FlashNotifications as FlashMessage} from "flash-notifications"
import {Form} from "@kaspernj/api-maker/build/form"
import I18n from "shared/i18n"
import {LocalesSelectWithContainer} from "components/locales/select"
import memo from "set-state-compare/build/memo"
import RadioButton from "components/utils/radio-button"
import Routes from "shared/routes"
import setLayout from "shared/set-layout"
import {Text} from "shared/base"
import useCurrentUser from "@kaspernj/api-maker/build/use-current-user"
import useI18n from "i18n-on-steroids/build/src/use-i18n.js"
import useNow from "set-state-compare/build/use-now"
import usersUpdateSession from "components/users/update-session"
import UtilsButton from "components/utils/button"
import UtilsInput from "components/utils/input"
import {View} from "react-native"

/** @typedef {object} RoutesUserProfileProps */

/**
 * @typedef {object} RoutesUserProfileState
 * @property {boolean} acceptMarketing
 * @property {boolean} activityInClass
 * @property {unknown} color
 * @property {boolean} getStarted
 * @property {unknown} locale
 * @property {unknown} localeId
 * @property {unknown} notificationSetting
 * @property {boolean} notificationSettingLoaded
 * @property {unknown} user
 * @property {boolean} userLoadFailed
 * @property {boolean} weeklyReportForYourClasses
 */

/**
 * @extends {ShapeComponent<RoutesUserProfileProps, RoutesUserProfileState>}
 */
class RoutesUserProfile extends ShapeComponent {
  /** @type {RoutesUserProfileState} */
  state = {
    acceptMarketing: true,
    activityInClass: true,
    color: undefined,
    getStarted: true,
    locale: undefined,
    localeId: undefined,
    notificationSetting: undefined,
    notificationSettingLoaded: false,
    user: undefined,
    userLoadFailed: false,
    weeklyReportForYourClasses: true
  }

  setup() {
    setLayout("user")

    const {t} = useI18n({namespace: "js.routes.user.profile"})
    const currentUser = useCurrentUser()
    const formRef = useRef()

    this.currentUser = currentUser
    this.formRef = formRef
    this.localesQuery = useMemo(() => Locale.ransack().select({Locale: ["locale"]}), [])

    useNow(() => {
      if (currentUser) {
        this.loadUser()
      } else {
        // useCurrentUser() returned nil - reset any previously loaded user and surface the error.
        this.s.user = undefined
        this.s.userLoadFailed = true
      }
    }, [currentUser?.id()])

    this.t = t
  }

  loadUser = async () => {
    const user = await User
      .ransack({id_eq: this.tt.currentUser.id()})
      .preload(["locale_model", "notification_setting"])
      .first()

    if (!user) {
      // Desync window: the cached current user is stale and its row isn't accessible. Show an error;
      // SessionStatusUpdater will sign out centrally and the requireUser layout will show sign-in.
      this.s.userLoadFailed = true

      return
    }

    const notificationSetting = user.notificationSetting() || new NotificationSetting()

    this.s.acceptMarketing = notificationSetting.acceptMarketing() || false
    this.s.activityInClass = notificationSetting.activityInClass() || false
    this.s.color = user.color()
    this.s.getStarted = notificationSetting.getStarted() || false
    this.s.locale = user.localeModel()?.locale() || I18n.locale
    this.s.localeId = user.localeModel()?.id()
    this.s.notificationSetting = notificationSetting
    this.s.notificationSettingLoaded = true
    this.s.user = user
    this.s.userLoadFailed = false
    this.s.weeklyReportForYourClasses = notificationSetting.weeklyReportForYourClasses() || false
  }

  render() {
    const {formRef, t} = this.tt
    const {user} = this.s

    if (this.s.userLoadFailed) {
      return (
        <View dataSet={this.rootDataSet ||= {component: "routes/user/profile"}}>
          <Card header={t("js.shared.error")}>
            <Text>{t(".user_could_not_be_loaded")}</Text>
          </Card>
        </View>
      )
    }

    if (!user) {
      return (
        <View dataSet={this.rootDataSet ||= {component: "routes/user/profile"}} />
      )
    }

    const form = formRef.current
    const emailValue = form?.getValue("user[email]") ?? (user?.hasUnconfirmedEmail() ? user?.unconfirmedEmail() : user?.email())
    const firstNameValue = form?.getValue("user[first_name]") ?? user?.firstName()
    const initialsValue = form?.getValue("user[initials]") ?? user?.initials()
    const lastNameValue = form?.getValue("user[last_name]") ?? user?.lastName()
    const currentPasswordValue = form?.getValue("user[current_password]")
    const passwordValue = form?.getValue("user[password]")
    const passwordConfirmationValue = form?.getValue("user[password_confirmation]")

    return (
      <View dataSet={this.rootDataSet ||= {component: "routes/user/profile"}}>
        <Form formObjectRef={this.tt.formRef}>
          <View style={{alignSelf: "center", width: "100%", maxWidth: 700}}>
            {this.renderUserInformationCard({emailValue, firstNameValue, initialsValue, lastNameValue})}
            {this.renderPasswordCard({currentPasswordValue, passwordValue, passwordConfirmationValue})}
            {this.renderNotificationsCard()}
            {this.renderActions()}
          </View>
        </Form>
      </View>
    )
  }

  renderUserInformationCard = ({emailValue, firstNameValue, initialsValue, lastNameValue}) => {
    const {t} = this.tt

    return (
      <Card header={t(".user_information")} style={{marginBottom: 10}}>
        <UtilsInput
          dataSet={{class: "email-input"}}
          defaultValue={emailValue}
          label={ContactEmail.humanAttributeName("email")}
          name="user[email]"
          style={{marginBottom: 10}}
        />
        <UtilsInput
          dataSet={{class: "first-name-input"}}
          defaultValue={firstNameValue}
          label={Contact.humanAttributeName("firstName")}
          name="user[first_name]"
          style={{marginBottom: 10}}
        />
        <UtilsInput
          dataSet={{class: "last-name-input"}}
          defaultValue={lastNameValue}
          label={Contact.humanAttributeName("lastName")}
          name="user[last_name]"
          style={{marginBottom: 10}}
        />
        <View style={{marginBottom: 15}}>
          <UtilsInput
            dataSet={{class: "initials-input"}}
            defaultValue={initialsValue}
            label={User.humanAttributeName("initials")}
            name="user[initials]"
          />
        </View>
        <View>
          <CircleColorSelect
            defaultValue={this.s.color}
            id="color"
            label={User.humanAttributeName("color")}
            onChange={this.tt.onChangeColor}
          />
        </View>
        <View style={{marginTop: 20}}>
          <LocalesSelectWithContainer
            id="user_locale"
            label={User.humanAttributeName("locale")}
            onChange={this.tt.onLocaleChanged}
            query={this.tt.localesQuery}
            values={this.s.localeId ? [this.s.localeId] : undefined}
          />
        </View>
      </Card>
    )
  }

  renderPasswordCard = ({currentPasswordValue, passwordValue, passwordConfirmationValue}) => {
    const {t} = this.tt

    return (
      <Card header={t(".new_password")} style={{marginBottom: 15}}>
        <UtilsInput
          dataSet={{class: "current-password-input"}}
          defaultValue={currentPasswordValue}
          label={t(".current_password")}
          name="user[current_password]"
          secureTextEntry
          style={{marginBottom: 10}}
        />
        <UtilsInput
          dataSet={{class: "password-input"}}
          defaultValue={passwordValue}
          label={t(".password")}
          name="user[password]"
          secureTextEntry
          style={{marginBottom: 10}}
        />
        <UtilsInput
          dataSet={{class: "password-confirmation-input"}}
          defaultValue={passwordConfirmationValue}
          label={t(".confirm_password")}
          name="user[password_confirmation]"
          secureTextEntry
          style={{marginBottom: 10}}
        />
      </Card>
    )
  }

  renderNotificationsCard = () => {
    const {t} = this.tt

    return (
      <Card header={t(".notifications")} style={this.notificationsCardStyle ||= {marginBottom: 15}}>
        {this.s.notificationSettingLoaded &&
          <>
            <RadioButton
              checked={this.s.acceptMarketing}
              dataSet={this.acceptMarketingDataSet ||= {class: "accept-marketing-radio-button"}}
              label={NotificationSetting.humanAttributeName("acceptMarketing")}
              onChangeChecked={this.setStates.acceptMarketing}
              style={this.acceptMarketingStyle ||= {marginVertical: 2}}
            />
            <RadioButton
              checked={this.s.activityInClass}
              dataSet={this.activityInClassDataSet ||= {class: "activity-in-class-radio-button"}}
              label={NotificationSetting.humanAttributeName("activityInClass")}
              onChangeChecked={this.setStates.activityInClass}
              style={this.activityInClassStyle ||= {marginVertical: 2}}
            />
            <RadioButton
              checked={this.s.getStarted}
              dataSet={this.getStartedDataSet ||= {class: "get-started-radio-button"}}
              label={NotificationSetting.humanAttributeName("getStarted")}
              onChangeChecked={this.setStates.getStarted}
              style={this.getStartedStyle ||= {marginVertical: 2}}
            />
            <RadioButton
              checked={this.s.weeklyReportForYourClasses}
              dataSet={this.weeklyReportDataSet ||= {class: "weekly-report-for-your-classes-radio-button"}}
              label={NotificationSetting.humanAttributeName("weeklyReportForYourClasses")}
              onChangeChecked={this.setStates.weeklyReportForYourClasses}
              style={this.weekyReportStyle ||= {marginVertical: 2}}
            />
          </>
        }
      </Card>
    )
  }

  renderActions = () => {
    const {t} = this.tt

    return (
      <>
        <UtilsButton
          dataSet={this.saveButtonDataSet ||= {class: "save-profile-button"}}
          onPress={this.tt.onSavePressed}
          title={t("js.shared.save")}
        />
        <UtilsButton
          color="red"
          dataSet={this.deleteProfileButtonDataSet ||= {class: "delete-profile-button"}}
          onPress={this.tt.onDeletePressed}
          style={this.deleteProfileButtonStyle ||= {marginTop: 10}}
          title={t(".delete_profile")}
        />
      </>
    )
  }

  onChangeColor = ({options}) => this.setState({color: options[0]?.value})

  onDeletePressed = async () => {
    const {currentUser, t} = this.tt

    if (!confirm(t("js.shared.are_you_sure"))) return

    try {
      await currentUser.deleteProfile()
      await usersUpdateSession()
      FlashMessage.success(t(".your_profile_was_deleted"))
      AppHistory.push(Routes.rootPath())
    } catch (error) {
      FlashMessage.errorResponse(error)
    }
  }

  onLocaleChanged = ({options}) =>
    this.setState({
      locale: options[0]?.locale?.locale(),
      localeId: options[0]?.value
    })

  onSavePressed = async () => {
    const {currentUser, t} = this.tt
    const {locale} = this.s
    const formData = this.tt.formRef.current.asObject()
    const formUserAttributes = formData.user || {}
    const userAttributes = {
      ...formUserAttributes,
      color: this.s.color,
      locale,
      notification_setting_attributes: {
        accept_marketing: this.s.acceptMarketing,
        activity_in_class: this.s.activityInClass,
        get_started: this.s.getStarted,
        id: this.s.notificationSetting?.id(),
        weekly_report_for_your_classes: this.s.weeklyReportForYourClasses
      }
    }
    const email = (formUserAttributes.email || "").toLowerCase().trim()

    delete userAttributes.email

    if (email && email != currentUser.email() && email != currentUser.unconfirmedEmail()) {
      userAttributes.email_request = email
    }

    try {
      await User.profileSave({user: userAttributes})

      if (locale != I18n.locale) {
        await changeLocale(locale)
      }

      FlashMessage.success(t(".your_profile_was_saved"))
    } catch (error) {
      FlashMessage.errorResponse(error)
    }
  }
}

export default memo(shapeComponent(RoutesUserProfile))
